import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CHARGE_LABEL_MAX,
  CHARGE_TYPES,
  RENT_CYCLES,
  chargeLabel,
  chargesFor,
  cycleSuffix,
  leaseEndFor,
  parseMoney,
  type ChargeType,
  type RentCycle,
  type UnitCharge,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { resolveParty, resolveUnit, searchParties, searchVacantUnits } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { QuickAddButton, QuickAddTenant, QuickAddUnit } from "@/components/quick-add";
import { titleCase, useFormat, useToday } from "@/lib/format";
import { useSession } from "@/lib/session";
import { Button, Card, CardTitle, Detail, Field, Input, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";
import { IconTrash } from "@/components/icons";
import { Refusal } from "@/components/padlock";
import { MoneyInput } from "@/components/money-input";
import { BeneficiaryPicker } from "@/components/beneficiary-picker";

/**
 * Creating a tenancy.
 *
 * The right-hand side is the point of this screen: the exact schedule of invoices the tenancy
 * will generate, recalculated as the terms change, *before* anything is written. Billing that
 * happens invisibly is billing nobody trusts, and a wrong agency fee found a week later is a
 * phone call nobody wants to make.
 */

interface ChargeRow {
  /**
   * The row's own identity, so React keys on the row rather than on where it sits.
   *
   * Keying by array index tells React that the third row is still the third row after the second
   * one is removed, and it hands the third row's rendered controls to what is now the second
   * charge. Everything here is driven from `charges`, so today that shows as a redraw rather than
   * as a wrong figure — but the moment a control holds anything of its own, a half-typed amount
   * lands on the neighbouring charge, and the stored record is right while only the screen is
   * wrong. Local to this form and never sent: `payload` builds what the API is given field by
   * field, and this is not one of the fields.
   */
  id: string;
  type: ChargeType;
  /**
   * What this charge is called, for the one type that does not say.
   *
   * Held on every row rather than only on the `other` ones so that changing the type to something
   * else and back does not lose what was typed — the row is still the same row, and somebody who
   * changed their mind twice should not have to write it again. Only an `other` charge sends it,
   * and `normaliseLeaseCharge` on the way in would drop a stray one anyway.
   */
  label: string;
  basis: "fixed" | "percent_of_rent";
  /** What the user typed: major-unit money for `fixed`, a percentage for `percent_of_rent`. */
  input: string;
  frequency: "once" | "per_period";
  /**
   * Whose money this charge is.
   *
   * Chosen on the row, and seeded from the unit's own advertised charge where the agency has
   * already answered the question on the building — a legal fee advertised as the lawyer's arrives
   * here as the lawyer's, and asking again on every tenancy drawn against that unit is how the same
   * lawyer ends up on half of them and nobody on the rest.
   *
   * Left alone, it is null and the server falls to `withBeneficiaries()`, which is the same answer
   * it gave before this control existed: rent and the caution deposit to the landlord of the unit,
   * the agency's own fees to the agency, and a legal fee to nobody, because the software has no way
   * to know who drew the agreement and a guess would put a real name on a real invoice.
   */
  beneficiaryId?: string | null;
}

interface DraftInvoice {
  key: string;
  issueDate: string;
  dueDate: string;
  periodStart?: string;
  periodEnd?: string;
  lines: Array<{ chargeType: string; description: string; amount: number }>;
  total: number;
}

let issued = 0;
const nextChargeId = () => `charge-${(issued += 1)}`;

const defaultCharges = (): ChargeRow[] => [
  { id: nextChargeId(), type: "agency_fee", label: "", basis: "percent_of_rent", input: "10", frequency: "once" },
  { id: nextChargeId(), type: "legal_fee", label: "", basis: "percent_of_rent", input: "10", frequency: "once" },
  { id: nextChargeId(), type: "deposit", label: "", basis: "percent_of_rent", input: "10", frequency: "once" },
];

export function LeaseNewPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { vocab, org } = useSession();
  const fmt = useFormat();
  const today = useToday();

  const [unitId, setUnitId] = useState(search.get("unitId") ?? "");
  const [tenantPartyId, setTenantPartyId] = useState(search.get("tenantId") ?? "");
  /** The two dialogs that make what the pickers cannot find. */
  const [addingUnit, setAddingUnit] = useState(false);
  const [addingTenant, setAddingTenant] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [rent, setRent] = useState("");
  const [cycle, setCycle] = useState<RentCycle>("annual");
  /** How many cycles make one billing period — see `components/term-input.tsx`. */
  const [every, setEvery] = useState(1);
  const [cycles, setCycles] = useState("1");
  const [paymentTerms, setPaymentTerms] = useState<"advance" | "arrears">("advance");
  const [graceDays, setGraceDays] = useState("7");
  /**
   * When the rent is to be looked at again, if the agreement says.
   *
   * Blank by default and never worked out from the term. A three-year tenancy reviewed annually
   * and a three-year tenancy at a fixed rent are both ordinary, and filling this in on the
   * agency's behalf would put a date on the record that nobody agreed to.
   */
  const [reviewDate, setReviewDate] = useState("");
  const [charges, setCharges] = useState<ChargeRow[]>(defaultCharges);
  const [notes, setNotes] = useState("");

  const { data: vacant, isLoading: unitsLoading } = useQuery({
    queryKey: ["vacant-units"],
    queryFn: () =>
      api.get<{
        units: Array<{
          id: string;
          label: string;
          propertyName: string;
          rentAmount: number | null;
          rentCycle: string;
          rentCycleEvery?: number;
          charges: UnitCharge[];
        }>;
      }>("/units/vacant"),
  });

  const { data: tenants } = useQuery({
    queryKey: ["parties-lookup", "tenant"],
    queryFn: () => api.get<{ parties: Array<{ id: string; name: string; phone: string | null }> }>("/parties/lookup", { role: "tenant" }),
  });

  const rentMinor = parseMoney(rent) ?? 0;

  /** The payload the preview and the create call share, so what you saw is what is written. */
  const payload = useMemo(
    () => ({
      unitId,
      tenantPartyId,
      startDate,
      cycles: Number(cycles) || 1,
      rentAmount: rentMinor,
      rentCycle: cycle,
      rentCycleEvery: every,
      paymentTerms,
      graceDays: Number(graceDays) || 0,
      depositAmount: 0,
      notes,
      charges: charges
        .filter((charge) => charge.input.trim() !== "")
        .map((charge) => ({
          type: charge.type,
          basis: charge.basis,
          value:
            charge.basis === "percent_of_rent"
              ? Math.round(Number(charge.input) * 100)
              : (parseMoney(charge.input) ?? 0),
          frequency: charge.frequency,
          /* Sent only when one was chosen, on this row or on the unit the charge came from.
             Absent, the server chooses the default for the type, which is the same answer it gave
             before this field existed — and "not yet decided" is a real state on a legal fee. */
          ...(charge.beneficiaryId ? { beneficiaryId: charge.beneficiaryId } : {}),
          /*
            The key is sent only when there is something to send, so a charge nobody named is
            written exactly as it was written before this field existed — no `"label": null` in the
            stored JSON, and nothing for the preview's cache key to churn on. The API drops a label
            on any type but `other` regardless; not sending one is simply the honest request.
          */
          ...(charge.type === "other" && charge.label.trim() ? { label: charge.label.trim() } : {}),
        })),
    }),
    [unitId, tenantPartyId, startDate, cycles, rentMinor, cycle, paymentTerms, graceDays, notes, charges],
  );

  const canPreview = rentMinor > 0 && Boolean(startDate);

  const { data: preview, isFetching: previewing } = useQuery({
    queryKey: ["lease-preview", JSON.stringify({ ...payload, unitId: "", tenantPartyId: "" })],
    queryFn: () =>
      api.post<{ endDate: string; schedule: DraftInvoice[]; commencementTotal: number; contractValue: number; cycles: number }>(
        "/leases/preview",
        { ...payload, unitId: unitId || "preview", tenantPartyId: tenantPartyId || "preview" },
      ),
    enabled: canPreview,
  });

  const create = useApiMutation({
    /*
      The review date is added here rather than carried in `payload`, and that is the reason
      `payload` exists: it is the request the preview is keyed on, so anything in it that does not
      change the schedule sends the manager's schedule away and fetches an identical one back while
      they are still typing. A review date changes nothing about what is billed — it is a date to
      keep, not a term of the billing — so it joins the request at the one moment it is written.
      Null rather than an empty string, so "nobody has said" is one value on the wire as well as
      one value in the column.
    */
    mutationFn: () => api.post<{ id: string; invoicesRaised: number }>("/leases", { ...payload, reviewDate: reviewDate || null }),
    invalidates: [["leases"], ["units"], ["dashboard"], ["invoices"]],
    onSuccess: (data) => navigate(`/app/tenancies/${data.id}`),
  });

  const selectedUnit = vacant?.units.find((unit) => unit.id === unitId);
  const endDate = preview?.endDate ?? (startDate ? leaseEndFor(startDate, cycle, Number(cycles) || 1) : "");

  /** Picking a unit fills the terms from what the unit is advertised at — still fully editable. */
  const chooseUnit = (id: string) => {
    setUnitId(id);
    const unit = vacant?.units.find((u) => u.id === id);
    if (!unit) return;
    if (unit.rentAmount) setRent(String(unit.rentAmount / 100));
    if (unit.rentCycle) setCycle(unit.rentCycle as RentCycle);
    if (unit.rentCycleEvery) setEvery(Number(unit.rentCycleEvery) || 1);
    /*
      The unit's own charges, brought across.

      Only the ones it advertises against renting: a commission quoted on the asking price has
      nothing to do with the tenancy being drawn up here. The two models line up field for field —
      minor units when fixed, basis points when a percentage — so this is a rename rather than a
      conversion, and everything stays editable afterwards.

      A deposit is not brought across as a charge. It has its own field on this form.
    */
    const brought = chargesFor(unit.charges, "rent").filter((charge) => charge.type !== "deposit");
    setCharges((current) => [
      ...current.filter((charge) => !brought.some((entry) => entry.type === charge.type)),
      ...brought.map((charge) => ({
        id: nextChargeId(),
        type: charge.type,
        // The name comes across with the charge it names. A unit advertised with a "Estate
        // development levy" is advertising that levy, and re-typing it on every tenancy drawn up
        // against the unit is how the same charge ends up under three different names.
        label: charge.label ?? "",
        basis: charge.basis === "percent" ? ("percent_of_rent" as const) : ("fixed" as const),
        // Divided by a hundred either way, but for two different reasons: minor units to major, or
        // basis points to per cent. Written as one expression because it is one expression, not
        // because the two cases are the same thing.
        input: String(charge.value / 100),
        frequency: charge.type === "service_charge" ? ("per_period" as const) : ("once" as const),
        // Whose it is comes across with it. The unit is where the agency already answered "who
        // draws the agreement on this building", and asking again on every tenancy is how the
        // same lawyer ends up on half of them and nobody on the rest.
        beneficiaryId: charge.beneficiaryId ?? null,
      })),
    ]);
  };

  return (
    <div>
      <PageHeader
        title={`New ${vocab.lease}`}
        back={{ to: "/app/tenancies", label: titleCase(vocab.leases) }}
        subtitle="Nothing is billed until you commit. The schedule on the right updates as you type."
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardTitle>Terms</CardTitle>
          <div className="space-y-4">
            {/*
              Letting a padlocked unit is one of the writes the API refuses with a 402 carrying its
              own gate, and a plan limit reached is not an error — it is the moment to offer the next
              plan. `Refusal` makes that one decision: the padlock and the way out of it when the
              refusal named a plan, the ordinary red box for everything else. Red on a limit teaches
              people to dismiss the box, and two components would eventually say the same sentence
              in two different tones.
            */}
            <Refusal error={create.error} message={create.errorMessage} />

            {/*
              Both pickers can make what they are looking for.
              Half the time one of them does not exist yet — a new tenant signing for a flat added
              this morning — and sending somebody to another screen to make it loses the dates, the
              rent and the charges they have already typed here.
            */}
            <Field
              label={titleCase(vocab.unit)}
              htmlFor="unitId"
              required
              hint="Only vacant, reserved and on-notice units are offered."
            >
              <SearchSelect
                id="unitId"
                value={unitId}
                onChange={(next) => chooseUnit(next)}
                search={searchVacantUnits}
                resolve={resolveUnit}
                placeholder={`Search for a ${vocab.unit}…`}
                emptyMessage={`No available ${vocab.unit} matched.`}
              />
              <span className="mt-1 block text-xs text-ink-500">
                Not there yet? <QuickAddButton onClick={() => setAddingUnit(true)}>Add a {vocab.unit}</QuickAddButton>.
              </span>
            </Field>

            <Field label={titleCase(vocab.tenant)} htmlFor="tenantPartyId" required>
              <SearchSelect
                id="tenantPartyId"
                value={tenantPartyId}
                onChange={(next) => setTenantPartyId(next)}
                search={searchParties("tenant")}
                resolve={resolveParty}
                placeholder={`Search for a ${vocab.tenant}…`}
                emptyMessage={`No ${vocab.tenant} matched.`}
              />
              <span className="mt-1 block text-xs text-ink-500">
                Not on the list?{" "}
                <QuickAddButton onClick={() => setAddingTenant(true)}>Add a {vocab.tenant}</QuickAddButton>.
              </span>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Starts" htmlFor="startDate" required>
                <Input id="startDate" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
              </Field>
              <Field label="Ends" htmlFor="endDate" hint="Worked out from the term below.">
                <Input id="endDate" value={endDate} disabled />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={titleCase(vocab.rent)} htmlFor="rent" required hint={`Per ${cycleSuffix(cycle).replace("per ", "")}.`}>
                <MoneyInput id="rent" value={rent} onChange={setRent} placeholder="3,500,000" />
              </Field>
              <Field label="Paid" htmlFor="cycle">
                <Select id="cycle" value={cycle} onChange={(event) => setCycle(event.target.value as RentCycle)}>
                  {RENT_CYCLES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="For how many" htmlFor="cycles" hint="Terms, not months.">
                <Input id="cycles" inputMode="numeric" placeholder="1" value={cycles} onChange={(event) => setCycles(event.target.value)} />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Payment terms" htmlFor="paymentTerms">
                <Select id="paymentTerms" value={paymentTerms} onChange={(event) => setPaymentTerms(event.target.value as "advance" | "arrears")}>
                  <option value="advance">In advance</option>
                  <option value="arrears">In arrears</option>
                </Select>
              </Field>
              <Field label="Grace days" htmlFor="graceDays" hint="Days after the due date before it counts as late.">
                <Input id="graceDays" inputMode="numeric" placeholder="0" value={graceDays} onChange={(event) => setGraceDays(event.target.value)} />
              </Field>
            </div>

            {/*
              The review, beside the term rather than among the charges.

              It is the other date that falls due on a tenancy, and the one that is forgotten: an
              expiry announces itself because the tenant is still in the flat, while a review is a
              clause nobody is reminded of, and a review missed is a year let at last year's figure.
              Left blank on purpose where the agreement is silent — this is not derived from the
              term, because a fixed-rent term and an annually reviewed one look identical from here.
            */}
            <Field
              label="Rent review"
              htmlFor="reviewDate"
              hint="Optional. The day the rent is to be looked at again, if the agreement names one."
            >
              <Input id="reviewDate" type="date" value={reviewDate} onChange={(event) => setReviewDate(event.target.value)} />
            </Field>

            <div>
              <p className="label">Charges</p>
              <p className="mb-2 text-xs text-ink-500">
                A percentage is taken of the {vocab.rent}. “Once” bills at commencement; “each period” bills alongside
                every {vocab.rent} invoice.
              </p>
              <div className="space-y-2">
                {charges.map((charge, index) => (
                  /*
                    The kind of charge takes its own line.
                    Five controls across a half-width card left the first one about 120px wide,
                    which truncated "Agency fee" to "Agency f" — a control that cannot show its
                    own value. "Legal / agreement fee" needs the whole width, and no arrangement
                    of five columns gives it that, so it gets a line of its own and the amount,
                    the unit and the timing share the one below. The border holds the two lines
                    together as one charge; "Remove" is a mark rather than a word so it stops
                    taking seventy pixels off the fields that need them.
                  */
                  <div
                    key={charge.id}
                    className="grid grid-cols-[minmax(0,1fr)_5rem_7rem_2.25rem] items-center gap-2 rounded-[var(--radius-control)] border border-ink-200 p-2"
                  >
                    <Select
                      className="col-span-4"
                      value={charge.type}
                      onChange={(event) =>
                        setCharges((rows) => rows.map((row, i) => (i === index ? { ...row, type: event.target.value as ChargeType } : row)))
                      }
                    >
                      {CHARGE_TYPES.filter((type) => type.value !== "rent" && type.value !== "late_fee").map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                    {/*
                      "Other" is the one type that does not say what it is, so it — and only it —
                      is given a line to say. The placeholder is the word the charge would carry
                      anyway, which is what makes leaving it empty a real choice rather than an
                      omission: the tenant reads "Other" either way, exactly as they did before
                      this field existed. Sixty characters because this ends up in a table cell,
                      on an invoice line and in a price breakdown on a phone.
                    */}
                    {charge.type === "other" ? (
                      <Input
                        className="col-span-4"
                        value={charge.label}
                        maxLength={CHARGE_LABEL_MAX}
                        placeholder="Other"
                        aria-label="What this charge is called"
                        onChange={(event) =>
                          setCharges((rows) => rows.map((row, i) => (i === index ? { ...row, label: event.target.value } : row)))
                        }
                      />
                    ) : null}
                    {/*
                      Who this money is for, on the row that records it.

                      Under the charge rather than beside it, because the answer belongs to the
                      charge above it and a fifth column would take width from the amount on a
                      laptop and wrap on a phone. The word is "For" and not "Beneficiary": the
                      reader is looking at a row that says "Agreement fee · 10% · once", and "For"
                      finishes that sentence.
                    */}
                    <div className="col-span-4 flex items-center gap-2">
                      <span className="shrink-0 text-xs text-ink-500">For</span>
                      <div className="min-w-0 flex-1">
                        <BeneficiaryPicker
                          id={`charge-beneficiary-${index}`}
                          ariaLabel="Who this charge is for"
                          value={charge.beneficiaryId ?? ""}
                          onChange={(next) =>
                            setCharges((rows) => rows.map((row, i) => (i === index ? { ...row, beneficiaryId: next } : row)))
                          }
                        />
                      </div>
                    </div>
                    {charge.basis === "percent_of_rent" ? (
                      <Input
                        value={charge.input}
                        onChange={(event) =>
                          setCharges((rows) => rows.map((row, i) => (i === index ? { ...row, input: event.target.value } : row)))
                        }
                        inputMode="decimal"
                        className="text-right"
                        placeholder="10"
                      />
                    ) : (
                      <MoneyInput
                        value={charge.input}
                        onChange={(input) =>
                          setCharges((rows) => rows.map((row, i) => (i === index ? { ...row, input } : row)))
                        }
                        className="text-right"
                        placeholder="250,000"
                      />
                    )}
                    <Select
                      value={charge.basis}
                      onChange={(event) =>
                        setCharges((rows) =>
                          rows.map((row, i) => (i === index ? { ...row, basis: event.target.value as ChargeRow["basis"] } : row)),
                        )
                      }
                    >
                      <option value="percent_of_rent">%</option>
                      <option value="fixed">{org?.currency ?? "NGN"}</option>
                    </Select>
                    <Select
                      value={charge.frequency}
                      onChange={(event) =>
                        setCharges((rows) =>
                          rows.map((row, i) => (i === index ? { ...row, frequency: event.target.value as ChargeRow["frequency"] } : row)),
                        )
                      }
                    >
                      <option value="once">Once</option>
                      <option value="per_period">Each period</option>
                    </Select>
                    <button
                      type="button"
                      aria-label="Remove this charge"
                      onClick={() => setCharges((rows) => rows.filter((_, i) => i !== index))}
                      className="flex size-9 items-center justify-center rounded-[var(--radius-control)] text-ink-400 transition-colors hover:bg-ink-100 hover:text-danger-600"
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() =>
                  setCharges((rows) => [
                    ...rows,
                    { id: nextChargeId(), type: "other", label: "", basis: "fixed", input: "", frequency: "once" },
                  ])
                }
              >
                Add a charge
              </Button>
            </div>

            <Field label="Notes" htmlFor="notes">
              <Input id="notes" placeholder="Anything worth remembering about this tenancy." value={notes} onChange={(event) => setNotes(event.target.value)} />
            </Field>
          </div>
        </Card>

        <div className="space-y-5">
          <Card>
            <CardTitle hint="Exactly what will be billed, and when.">The schedule</CardTitle>

            {!canPreview ? (
              <p className="py-6 text-center text-sm text-ink-500">Enter the {vocab.rent} to see the schedule.</p>
            ) : previewing && !preview ? (
              <Spinner label="Working it out…" />
            ) : preview ? (
              <>
                <dl className="mb-3">
                  <Detail label="Due at commencement">
                    <span className="font-semibold">{fmt.money(preview.commencementTotal)}</span>
                  </Detail>
                  <Detail label="Whole term">{fmt.money(preview.contractValue)}</Detail>
                  <Detail label="Ends">{fmt.date(preview.endDate)}</Detail>
                  <Detail label="Invoices">{preview.schedule.length}</Detail>
                </dl>

                <div className="max-h-96 overflow-y-auto rounded-lg border border-ink-200">
                  {preview.schedule.map((invoice) => (
                    <div key={invoice.key} className="border-b border-ink-100 p-3 last:border-0">
                      <div className="flex items-baseline justify-between">
                        <p className="text-sm font-medium">Due {fmt.date(invoice.dueDate)}</p>
                        <p className="tabular text-sm font-semibold">{fmt.money(invoice.total)}</p>
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {invoice.lines.map((line, index) => (
                          <li key={index} className="flex justify-between gap-3 text-xs text-ink-500">
                            <span className="truncate">
                              {/*
                                The line's description is the name the schedule already resolved for
                                it — `chargeLabel(type, label)` on the way out of `buildSchedule` —
                                so handing it back to the same function is how an "Other" charge
                                shows the agency's own words here rather than the word "Other".
                                Every other type ignores the second argument, which is why this is
                                safe to pass unconditionally: a rent line's description is a range
                                of dates and would be nonsense as a name.
                              */}
                              {chargeLabel(line.chargeType as ChargeType, line.description)}
                              {line.chargeType === "rent" && invoice.periodStart
                                ? ` · ${fmt.date(invoice.periodStart)} → ${fmt.date(invoice.periodEnd ?? null)}`
                                : ""}
                            </span>
                            <span className="tabular shrink-0">{fmt.money(line.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <p className="mt-3 text-xs text-ink-500">
                  Invoices falling due more than 45 days out are raised automatically as they approach, so the{" "}
                  {vocab.tenant} is not sent a year of paperwork at once.
                </p>
              </>
            ) : null}
          </Card>

          <Card>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!unitId || !tenantPartyId || rentMinor <= 0 || create.isPending}
                onClick={() => create.mutate(undefined as never)}
              >
                {create.isPending ? "Creating…" : `Create ${vocab.lease}`}
              </Button>
              <LinkButton to="/app/tenancies" variant="secondary">
                Cancel
              </LinkButton>
            </div>
            {!unitId || !tenantPartyId ? (
              <p className="mt-2 text-xs text-ink-500">
                Choose a {vocab.unit} and a {vocab.tenant} to continue.
              </p>
            ) : null}
            {selectedUnit ? (
              <p className="mt-2 text-xs text-ink-500">
                {selectedUnit.propertyName} · {selectedUnit.label} will be marked occupied.
              </p>
            ) : null}
          </Card>
        </div>
      </div>
      <QuickAddUnit
        open={addingUnit}
        onClose={() => setAddingUnit(false)}
        onCreated={(id) => chooseUnit(id)}
      />
      <QuickAddTenant
        open={addingTenant}
        onClose={() => setAddingTenant(false)}
        onCreated={(id) => setTenantPartyId(id)}
      />
    </div>
  );
}
